"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { scoreBadgeColor } from "@/lib/conditions/scoring";
import { conditionsSpotHref } from "@/lib/seo/conditions-links";
import { areaContains, type BoardAreaIndex } from "./board-area-model";

/**
 * The board's This week view (forecast areas spec, sections 5.13 and 10.2). It fetches the
 * stored weekly rankings from `/api/conditions/week` the first time it opens, so the board HTML
 * carries no extra weight. The area filter applies to these rows too, and the piste board can
 * group them by area for trip planners. The server only offers the view when the sport's
 * cross-spot week ranking is certified.
 */
export type BoardWeekRow = Readonly<{
  slug: string;
  displayName: string;
  areaSlug: string | null;
  countryCode: string;
  avg7d10: number;
  bestDayOffset: number;
  bestDay10: number;
  trend: "improving" | "steady" | "declining";
}>;

export type BoardWeekPayload = Readonly<{
  sport: string;
  gate: Readonly<{ certified: boolean; reason: string }>;
  dates: readonly string[];
  rows: readonly BoardWeekRow[];
}>;

type LoadState =
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "error" }>
  | Readonly<{ status: "ready"; payload: BoardWeekPayload }>;

const loaded = new Map<string, BoardWeekPayload>();

async function loadWeek(sport: string, signal: AbortSignal): Promise<BoardWeekPayload> {
  const cached = loaded.get(sport);
  if (cached) return cached;
  const response = await fetch(`/api/conditions/week?sport=${encodeURIComponent(sport)}`, { signal });
  if (!response.ok) throw new Error(`week ${response.status}`);
  const payload = (await response.json()) as BoardWeekPayload;
  loaded.set(sport, payload);
  return payload;
}

/**
 * "Sat 4 Oct" for an ISO date, read as a calendar date. The week dates are UTC run dates (day 0
 * is the UTC date the rankings were computed), not the venue's local date: a run just after
 * midnight UTC can label day 0 one calendar day off for Australia and New Zealand. Known and
 * accepted while the stored rankings carry no per-venue day boundaries.
 */
export function weekDayLabel(dateISO: string | undefined): string | null {
  if (!dateISO) return null;
  const date = new Date(`${dateISO}T12:00:00Z`);
  if (!Number.isFinite(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(date);
}

const TREND_LABELS: Readonly<Record<BoardWeekRow["trend"], { symbol: string; label: string; className: string }>> = {
  improving: { symbol: "▲", label: "Improving", className: "text-emerald-700" },
  steady: { symbol: "●", label: "Steady", className: "text-text-muted" },
  declining: { symbol: "▼", label: "Easing", className: "text-orange-700" },
};

/** Pure: rows in the area (or all), ranked by the seven-day average. */
export function weekRowsForArea(
  rows: readonly BoardWeekRow[],
  index: BoardAreaIndex | null,
  activeArea: string | null,
): readonly BoardWeekRow[] {
  const inArea = activeArea && index
    ? rows.filter((row) => areaContains(index, row.areaSlug, activeArea))
    : rows;
  return [...inArea].sort((a, b) => b.avg7d10 - a.avg7d10 || a.displayName.localeCompare(b.displayName, "en-GB"));
}

/** Pure: rows grouped by leaf area, groups ordered by their best average. */
export function groupWeekRowsByArea(
  rows: readonly BoardWeekRow[],
  areaNames: Readonly<Record<string, string>>,
): readonly Readonly<{ key: string; name: string; rows: readonly BoardWeekRow[] }>[] {
  const groups = new Map<string, BoardWeekRow[]>();
  for (const row of rows) {
    const key = row.areaSlug && areaNames[row.areaSlug] ? row.areaSlug : "";
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }
  return [...groups.entries()]
    .map(([key, list]) => ({ key, name: key ? areaNames[key] : "Other resorts", rows: list }))
    .sort((a, b) => (a.key === "" ? 1 : b.key === "" ? -1 : b.rows[0].avg7d10 - a.rows[0].avg7d10));
}

function WeekRow({
  row,
  rank,
  dates,
  lensSport,
}: {
  row: BoardWeekRow;
  rank: number;
  dates: readonly string[];
  lensSport: string | null;
}) {
  const badge = scoreBadgeColor(row.avg7d10);
  const trend = TREND_LABELS[row.trend];
  const bestDay = weekDayLabel(dates[row.bestDayOffset]);
  return (
    <li className="rounded-[16px] border border-border-light bg-white">
      <Link
        href={conditionsSpotHref(row.slug, lensSport)}
        className="flex items-center gap-3 p-4 no-underline md:gap-5 md:p-5"
      >
        <span className="w-8 shrink-0 font-serif text-[22px] font-bold tabular-nums text-navy md:w-10 md:text-[26px]">
          {rank}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-serif text-[17px] font-semibold leading-tight text-navy md:text-[19px]">
            {row.displayName}
          </span>
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-text-muted">
            {bestDay && (
              <span>
                Best day <strong className="font-semibold text-navy">{bestDay}</strong>, {scoreOutOf100(row.bestDay10)}
              </span>
            )}
            <span className={trend.className}>
              <span aria-hidden>{trend.symbol}</span> {trend.label}
            </span>
          </span>
        </span>
        <span className={`shrink-0 rounded-[10px] px-2.5 py-1.5 text-center ${badge.bg} ${badge.text}`}>
          <span className="block font-serif text-[18px] font-bold leading-none tabular-nums md:text-[22px]">
            {scoreOutOf100(row.avg7d10)}
          </span>
          <span className="mt-0.5 block text-[9px] font-semibold uppercase tracking-[1px] opacity-90">
            Week avg
          </span>
        </span>
      </Link>
    </li>
  );
}

export function BoardWeekView({
  sport,
  lensSport,
  index,
  activeArea,
  activeAreaName,
  groupByAreaAvailable,
}: {
  sport: string;
  lensSport: string | null;
  index: BoardAreaIndex | null;
  activeArea: string | null;
  activeAreaName: string | null;
  groupByAreaAvailable: boolean;
}) {
  const [state, setState] = useState<LoadState>(() => {
    const cached = loaded.get(sport);
    return cached ? { status: "ready", payload: cached } : { status: "loading" };
  });
  const [groupByArea, setGroupByArea] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (loaded.has(sport)) return;
    const controller = new AbortController();
    loadWeek(sport, controller.signal)
      .then((payload) => {
        if (!controller.signal.aborted) setState({ status: "ready", payload });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: "error" });
      });
    return () => controller.abort();
  }, [sport, attempt]);

  const rows = useMemo(
    () => (state.status === "ready" ? weekRowsForArea(state.payload.rows, index, activeArea) : []),
    [state, index, activeArea],
  );

  if (state.status === "loading") {
    return (
      <div className="rounded-[14px] border border-border-light bg-white p-8 text-center" role="status" data-board-week="loading">
        <p className="text-[15px] text-text-muted">Loading this week&apos;s rankings...</p>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="rounded-[14px] border border-border-light bg-white p-8 text-center" data-board-week="error">
        <p className="text-[15px] text-text-muted">This week&apos;s rankings could not be loaded.</p>
        <button
          type="button"
          onClick={() => {
            setState({ status: "loading" });
            setAttempt((value) => value + 1);
          }}
          className="mt-3 inline-flex min-h-11 cursor-pointer items-center rounded-full border border-border-light bg-white px-4 py-1.5 text-[13px] font-semibold text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal"
        >
          Try again
        </button>
      </div>
    );
  }

  const { payload } = state;
  const first = weekDayLabel(payload.dates[0]);
  const last = weekDayLabel(payload.dates[payload.dates.length - 1]);
  const groups = groupByArea && index ? groupWeekRowsByArea(rows, index.areaNames) : null;

  return (
    <section aria-label="This week" className="space-y-2.5 md:space-y-3" data-board-week="ready">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-text-muted">
          Ranked by the average score over the next seven days
          {first && last ? ` (${first} to ${last})` : ""}
          {sport === "sailing" ? " for club dinghies" : ""}
          {activeAreaName ? `, ${activeAreaName}` : ""}.
        </p>
        {groupByAreaAvailable && index && (
          <button
            type="button"
            aria-pressed={groupByArea}
            onClick={() => setGroupByArea((value) => !value)}
            className={`inline-flex min-h-11 cursor-pointer items-center rounded-full px-3.5 py-1.5 text-[12px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal ${
              groupByArea
                ? "border border-navy bg-navy text-white"
                : "border border-border-light bg-white text-text-body hover:border-navy/40 hover:text-navy"
            }`}
          >
            Group by area
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-[14px] border border-border-light bg-white p-8 text-center">
          <p className="text-[15px] text-text-muted">
            {activeAreaName
              ? `No week rankings for ${activeAreaName} yet. Try All areas.`
              : "This week's rankings are not ready yet. They refresh every few hours."}
          </p>
        </div>
      ) : groups ? (
        groups.map((group) => (
          <div key={group.key || "other"} className="space-y-2.5 pt-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[1.2px] text-text-body">{group.name}</h3>
            <ol className="space-y-2.5">
              {group.rows.map((row) => (
                <WeekRow key={row.slug} row={row} rank={rows.indexOf(row) + 1} dates={payload.dates} lensSport={lensSport} />
              ))}
            </ol>
          </div>
        ))
      ) : (
        <ol className="space-y-2.5 md:space-y-3">
          {rows.map((row, position) => (
            <WeekRow key={row.slug} row={row} rank={position + 1} dates={payload.dates} lensSport={lensSport} />
          ))}
        </ol>
      )}
    </section>
  );
}
